import { useEffect, useRef, useState } from 'react';

import useOutsideClick from '@/modules/shared/hooks/useOutsideClick';

interface DropDownOption {
  icon?: JSX.Element;
  text: string;
}

interface DropDownProps {
  options: DropDownOption[];
  selectedOption?: string;
  trigger: React.ReactNode;
  onSelect: (option: DropDownOption) => void;
  className?: string;
}

const DropDown: React.FC<DropDownProps> = ({ options, selectedOption, trigger, onSelect, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const divWhereToScroll = useRef<HTMLDivElement>(null);

  const toggleDropDown = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option: DropDownOption) => {
    onSelect(option);
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen && divWhereToScroll) {
      divWhereToScroll.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isOpen]);

  useOutsideClick(dropdownRef, () => setIsOpen(false));

  return (
    <div ref={dropdownRef} className="relative inline-block text-left z-20">
      <div onClick={toggleDropDown}>{trigger}</div>
      {isOpen && (
        <div
          className={`absolute -left-1 bottom-[100%] mb-2 w-44 z-10 mt-2 p-2 pb-2 px-2 border border-custom-mediumSilver rounded-lg bg-white shadow-[inset_0_1px_0_#ffffff,inset_1px_0_0_#ffffff,2px_2px_4px_rgba(0,0,0,0.05)] ${className}`}
          ref={divWhereToScroll}
        >
          {options.map((option, index) => (
            <div
              key={index}
              className={`group flex flex-row items-center mb-0.5 ${
                !option.icon ? 'justify-center' : ''
              } gap-2 px-2 py-3 cursor-pointer rounded hover:bg-custom-lightSilver text-custom-black ${
                selectedOption == option.text ? 'bg-custom-mediumSilver' : ''
              } ${option.text === 'Sign Out' ? 'buttonSignOut' : ''} ${
                option.text === 'My Account' ? 'buttonMyAccount' : ''
              }`}
              onClick={() => handleOptionClick(option)}
            >
              {option.icon && <span className="group-hover:[&>*_path]:stroke-custom-black">{option.icon}</span>}
              <span className="text-custom-black text-sm tracking-[0.0175rem]">{option.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DropDown;
